"use client"

import { Button } from "@/components/ui/button"
import React, { useState } from "react"
import Pagination from "./pagination"
import { Icon } from "@/components/ui/icon"
import Link from "@/components/local-link"

type SpecialityType = {
  name: string
  uid: string
}

type Props = {
  specialities: SpecialityType[]
  // other properties...
}

export default function SpecialitiesSection({ specialities }: Props) {
  const [activeLetter, setActiveLetter] = useState("")
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")
  const filteredSpecialities = activeLetter
    ? specialities.filter((speciality) =>
        speciality.name.startsWith(activeLetter)
      )
    : specialities
  const groupedSpecialities: { [key: string]: SpecialityType[] } =
    filteredSpecialities.reduce(
      (groups, speciality) => {
        const letter = speciality.name.charAt(0)
        if (!groups[letter]) {
          groups[letter] = []
        }
        groups[letter].push(speciality)
        return groups
      },
      {} as { [key: string]: SpecialityType[] }
    ) // Add index signature to the type declaration
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = Object.entries(groupedSpecialities).slice(
    indexOfFirstItem,
    indexOfLastItem
  )

  return (
    <section className="container flex flex-col gap-6">
      <div>
        {letters.map((letter) => (
          <Button
            variant={"outline"}
            size={"sm"}
            key={letter}
            onClick={() => {
              setActiveLetter((prev) => (prev === letter ? "" : letter))
              setCurrentPage(1)
            }}
            style={{
              backgroundColor: activeLetter === letter ? "#00B06F" : "white",
              color: activeLetter === letter ? "white" : "black",
              margin: "0.25rem",
            }}
          >
            {letter}
          </Button>
        ))}
      </div>
      <div>
        <Pagination
          totalItems={filteredSpecialities.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          currentPage={currentPage}
        />
        <div className="grid grid-cols-1">
          {currentItems.map(([letter, specialities]) => (
            <div key={letter}>
              <h2 className="text-left font-manrope text-4xl font-bold leading-relaxed text-green-600">
                {letter}
              </h2>
              <Icon name="app--separator" className="h-4 w-3/12 self-start" />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                {specialities.map((speciality) => (
                  <div key={speciality.name} className="pb-4 sm:pb-16">
                    <p className="text-left font-lato text-sm font-bold leading-normal sm:text-2xl">
                      {speciality.name}
                    </p>
                    <Link
                      href={`specialities/${speciality.uid}`}
                      className="text-left text-blue-600 underline"
                    >
                      Learn More
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
